import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useHistory, useParams } from 'react-router';
import { toast } from 'react-toastify';
import { getFormValues, isValid, touch } from 'redux-form';
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Row,
  TabContent,
  TabPane,
} from 'reactstrap';
import Loading from '../../shared/components/custom/Loading';
import NavTab from '../../shared/components/custom/NavTab';
import Api from '../../util/api';
import { partial } from '../../util/translation/translation';
import ChatAccessForm from './components/ChatAccessForm';
import ChatForm from './components/ChatForm';
import ChatMessagesList from './components/ChatMessagesList';

const p = partial('chat');
const s = partial('shared');
const e = partial('EventsForm');

const ChatFormPage = ({
  chatAccessFormValues,
  chatFormValues,
  chatAccessFormValid,
  chatFormValid,
  dispatch,
}) => {
  const { chatId } = useParams();
  const history = useHistory();
  const [groupsLevelsTiers, setGroupsLevelsTiers] = useState({
    tiers: [],
    groups: [],
    levels: [],
  });
  const [tab, setTab] = useState('0');
  const [chat, setChat] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingAccess, setLoadingAccess] = useState(false);
  const [chatAccess, setChatAccess] = useState({});

  const mapGroupsLevelsTiers = useCallback(
    (resLevels, resGroups, resTiers) => ({
      tiers: [
        { label: e('allTiers'), value: [] },
        { label: s('ignore'), value: null },
        ...resTiers.map((item) => ({ label: item.name, value: item.id })),
      ],
      groups: [
        { label: e('allGroups'), value: [] },
        { label: s('ignore'), value: null },
        ...resGroups.map((item) => ({ label: item.value, value: item.value })),
      ],
      levels: [
        { label: e('allLevels'), value: [] },
        { label: s('ignore'), value: null },
        ...resLevels.map((item) => ({ label: item.value, value: item.value })),
      ],
    }),
    []
  );

  const getChat = useCallback(async () => {
    setLoading(true);
    try {
      const resChat = await Api.chat.getChat(chatId);
      setChat(resChat);
    } catch (err) {
      toast.error(err || s('somethingWentWrong'));
    } finally {
      setLoading(false);
    }
  }, [chatId]);

  const getChatAccess = useCallback(async () => {
    setLoadingAccess(true);
    try {
      const [resAccess, resLevels, resGroups, { data: resTiers }] =
        await Promise.all([
          Api.chat.getChatAccess(chatId),
          Api.club.getLevels(),
          Api.club.getGroups(),
          Api.tiers.allTiers(),
        ]);

      setGroupsLevelsTiers(
        mapGroupsLevelsTiers(resLevels, resGroups, resTiers)
      );
      setChatAccess(resAccess);
    } catch (err) {
      toast.error(err || s('somethingWentWrong'));
    } finally {
      setLoadingAccess(false);
    }
  }, [chatId, mapGroupsLevelsTiers]);

  const getGroupsTiersLevels = useCallback(async () => {
    setLoadingAccess(true);
    try {
      const [resLevels, resGroups, { data: resTiers }] = await Promise.all([
        Api.club.getLevels(),
        Api.club.getGroups(),
        Api.tiers.allTiers(),
      ]);

      setGroupsLevelsTiers(
        mapGroupsLevelsTiers(resLevels, resGroups, resTiers)
      );
      setChatAccess({
        tiers: null,
        groups: null,
        levels: null,
        noAccessView: true,
        visible: true,
      });
    } catch (err) {
      toast.error(err || s('somethingWentWrong'));
    } finally {
      setLoadingAccess(false);
    }
  }, [mapGroupsLevelsTiers]);

  useEffect(() => {
    if (chatId) {
      getChat();
      getChatAccess();
    } else {
      getGroupsTiersLevels();
    }
  }, [chatId, getChat, getChatAccess, getGroupsTiersLevels]);

  const getAccessValues = (searchArray) => {
    if (searchArray.length === 0) {
      return null;
    }

    const foundItem = searchArray.find(
      (item) => Array.isArray(item.value) || item.value === null
    );

    if (foundItem) {
      return foundItem.value;
    }

    return searchArray.map((item) => item.value);
  };

  const onChatSubmit = async (values) => {
    const payload = {
      name: values.name,
      description: values.description || '',
      isActive: !!values.isActive,
      image: values.image.croppedBase64 || values.image.preview,
    };

    setLoading(true);
    try {
      if (chatId) {
        await Api.chat.updateChat(chatId, payload);
        return {
          finalId: chatId,
          isNew: false,
        };
      }

      const createChatResponse = await Api.chat.createChat(payload);
      return {
        finalId: createChatResponse.id,
        isNew: true,
      };
    } catch (err) {
      toast.error(err || s('somethingWentWrong'));
      return null;
    } finally {
      setLoading(false);
    }
  };

  const pollChatAccess = async (_chatId, callBack, iteration = 0) => {
    if (iteration > 10) {
      toast.error(p('updatingAccessFailed'));
      history.push('/chats');
      return;
    }

    try {
      await Api.chat.getChatAccess(_chatId);
      callBack();
    } catch {
      setTimeout(() => pollChatAccess(_chatId, callBack, iteration + 1), 1000);
    }
  };

  const onAccessSubmit = async (_chatId, values) => {
    const payload = {
      tiers: getAccessValues(values?.tier || []),
      groups: getAccessValues(values?.group || []),
      levels: getAccessValues(values?.level || []),
      noAccessView: values?.noAccessView || false,
      visible: typeof values?.visible === 'boolean' ? values.visible : true,
    };

    setLoadingAccess(true);
    try {
      await Api.chat.updateChatAccess(_chatId, payload);
      history.push('/chats');
      toast.success(p('chatUpdated'));
    } catch {
      toast.error(p('updatingAccessFailed'));
    } finally {
      setLoadingAccess(false);
    }
  };

  const handleChatSubmit = async () => {
    if (!chatFormValid) {
      dispatch(touch('chat_form', 'name', 'description', 'isActive'));
      return;
    }

    if (!chatAccessFormValid) {
      dispatch(touch('chat_access_form', 'level', 'group', 'tier'));
      return;
    }

    const chatData = await onChatSubmit(chatFormValues);
    if (!chatData) {
      return;
    }

    if (chatData.isNew) {
      setLoadingAccess(true);
      pollChatAccess(chatData.finalId, () =>
        onAccessSubmit(chatData.finalId, chatAccessFormValues)
      );
    } else {
      await onAccessSubmit(chatData.finalId, chatAccessFormValues);
    }
  };

  return (
    <Container>
      <Loading loading={loading || loadingAccess} />
      <Row>
        <Col>
          <Card>
            <CardBody className="p-0">
              <NavTab
                tabs={[
                  { tab: `${s('details')}` },
                  { tab: `${p('messages')}`, disabled: !chatId },
                ]}
                setTab={setTab}
                activeTab={tab}
                size="sm"
              />
              <TabContent activeTab={tab}>
                <TabPane tabId="1" className="m-3">
                  <ChatForm chat={chat} />
                  <ChatAccessForm
                    access={chatAccess}
                    groupsLevelsTiers={groupsLevelsTiers}
                  />
                  <Col md={12}>
                    <Button color="primary" onClick={handleChatSubmit}>
                      {chatId ? s('save') : s('create')}
                    </Button>
                    <Button
                      type="button"
                      onClick={() => history.push('/chats')}
                    >
                      {s('close')}
                    </Button>
                  </Col>
                </TabPane>
                <TabPane tabId="2" className="m-3">
                  <ChatMessagesList chatId={chatId} />
                </TabPane>
              </TabContent>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

const mapStateToProps = (state) => ({
  chatAccessFormValues: getFormValues('chat_access_form')(state),
  chatFormValues: getFormValues('chat_form')(state),
  chatAccessFormValid: isValid('chat_access_form')(state),
  chatFormValid: isValid('chat_form')(state),
});

export default connect(mapStateToProps)(ChatFormPage);
